import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";

import { Section } from "../../styled-components";
import context from "../../context";
import { RightOutlined, LeftOutlined } from "@ant-design/icons";
import AniLink from "gatsby-plugin-transition-link/AniLink";

// ✅ fallback local (webpack)
import fallbackImg from "../../images/prueba.png";

/* ---------- estilos ---------- */

const Overline = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  letter-spacing: 0.12em;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 8px;

  &:before {
    content: "";
    display: inline-block;
    width: 12px;
    height: 12px;
    border-left: 6px solid ${(p) => p.theme.primaryColor};
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 0;
  }
`;

const H1 = styled.h3`
  font-size: clamp(1.6rem, 3.6vw, 3rem);
  line-height: 1.2;
  font-weight: 500;
  color: #2b2b2b;
  margin: 0 0 20px;
`;

const FullBleed = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;

  --side-pad: clamp(1rem, 6vw, 5rem);
  padding-left: var(--side-pad);
  padding-right: var(--side-pad);
`;

const Rail = styled.div`
  position: relative;
  margin-top: 16px;
`;

const SliderCustom = styled(Slider)`
  .carousel__inner-slide {
    padding: 0 16px;
  }
`;

const CardLink = styled(AniLink)`
  display: block;
  text-decoration: none;
  color: inherit;

  /* accesibilidad y feedback */
  border-radius: 6px;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.2);
  }
`;

const Card = styled.figure`
  margin: 0;
  background: #fff;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  ${CardLink}:hover & {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.12);
  }
`;

const Img = styled.img`
  display: block;
  width: 100%;
  height: clamp(200px, 36vw, 340px);
  object-fit: cover;
  background: #f3f4f6;
`;

const Figcaption = styled.figcaption`
  padding: 12px 16px 16px;
  color: #1f2937;
`;

const CaptionTitle = styled.div`
  font-weight: 800;
  margin-bottom: 6px;
`;

const CaptionText = styled.div`
  font-size: 0.98rem;
  color: #374151;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`;

const Arrow = styled.button`
  position: absolute;
  top: 45%;
  transform: translateY(-50%);
  z-index: 2;
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${(p) => p.theme.primaryColor};
  color: #fff;
  cursor: pointer;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(1.05);
  }

  &.prev {
    left: calc(var(--side-pad) - 0.25rem);
  }
  &.next {
    right: calc(var(--side-pad) - 0.25rem);
  }

  @media (max-width: 576px) {
    width: 34px;
    height: 34px;
  }
`;

/* ---------- componente ---------- */

export default function ClientExperience() {
  const state = useContext(context);

  const items = useMemo(() => {
    const src = state?.home?.reviews?.items;

    if (Array.isArray(src) && src.length) {
      return src.map((r) => ({
        image: r.image || fallbackImg,
        author: r.author || "Cliente",
        review: r.review || "",
      }));
    }

    return [
      { image: fallbackImg, author: "Cliente 1", review: "Lorem ipsum..." },
      { image: fallbackImg, author: "Cliente 2", review: "Lorem ipsum..." },
      { image: fallbackImg, author: "Cliente 3", review: "Lorem ipsum..." },
    ];
  }, [state]);

  return (
    <Section>
      <FullBleed>
        <Overline>SERVICIO ENTREGADO</Overline>
        <H1>EXPERIENCIA DE CLIENTES</H1>

        <Rail>
          <CarouselProvider
            naturalSlideWidth={100}
            isIntrinsicHeight
            totalSlides={items.length}
            visibleSlides={3}
            infinite
          >
            <SliderCustom>
              {items.map((it, idx) => (
                <Slide key={idx} index={idx}>
                  {/* ✅ CLICK -> /resenas */}
                  <CardLink paintDrip hex={state?.primaryColor} to="/resenas" duration={0.5}>
                    <Card>
                      <Img src={it.image} alt={it.author} />
                      <Figcaption>
                        <CaptionTitle>{it.author}</CaptionTitle>
                        <CaptionText>{it.review}</CaptionText>
                      </Figcaption>
                    </Card>
                  </CardLink>
                </Slide>
              ))}
            </SliderCustom>

            <Arrow as={ButtonBack} className="prev" aria-label="Anterior">
              <LeftOutlined />
            </Arrow>
            <Arrow as={ButtonNext} className="next" aria-label="Siguiente">
              <RightOutlined />
            </Arrow>
          </CarouselProvider>
        </Rail>
      </FullBleed>

      <style>{`
        @media (max-width: 991px){
          .carousel__slider-tray .carousel__slide { width: 50% !important; }
        }
        @media (max-width: 640px){
          .carousel__slider-tray .carousel__slide { width: 100% !important; }
        }
      `}</style>
    </Section>
  );
}